import Num from '@flipster/utils/num';

/**
 * ERD-233 · Market Order Slippage Tolerance Setting (Web)
 * https://app.notion.com/p/37beb32ad227812a8413e41a3de491c2
 */

export type SlippageMode = 'percent' | 'price';

export const minUserSlippage = 0.0001;
export const maxUserSlippage = 0.05;

export const minUserSlippagePercent = minUserSlippage * 100; // 0.01
export const maxUserSlippagePercent = maxUserSlippage * 100; // 5

export function percentToRatio(percent: string): string {
  const num = Num(percent);
  if (!num.isFinite()) return '0';
  return num.div(100).toString();
}

export function isPercentInRange(percent: string): boolean {
  const num = Num(percent);
  if (!num.isFinite()) return false;
  return num.isGreaterThanOrEqualTo(minUserSlippagePercent) && num.isLessThanOrEqualTo(maxUserSlippagePercent);
}

export function clampPercentToMax(percent: string): string {
  const num = Num(percent);
  if (!num.isFinite()) return percent;
  if (num.isGreaterThan(maxUserSlippagePercent)) return String(maxUserSlippagePercent);
  return percent;
}

/**
 * Real-time input cap for the By-price SPREAD: caps to reference·5% (the max-equivalent) while typing.
 * Below the cap the original string is returned unchanged (preserves in-progress formatting like "50.").
 * Note: this is the ONLY spread cap — `buildSlippageRequestFields` sends `BBO ± value` raw at submit
 * (no re-cap); the absolute reference±5% limit is then enforced server-side.
 */
export function clampSpreadToMax(spread: string, referencePrice: string): string {
  const reference = Num(referencePrice);
  const value = Num(spread);
  if (!value.isFinite() || !reference.isFinite() || reference.isLessThanOrEqualTo(0)) return spread;
  const maxSpread = reference.times(maxUserSlippage);
  if (value.isGreaterThan(maxSpread)) return maxSpread.toString();
  return spread;
}

export type SlippageSide = 'Long' | 'Short' | 'unknown';

export interface SlippageState {
  enabled: boolean;
  mode: SlippageMode;
  value: string;
}

export interface SlippageRequestFields {
  maxSlippageTolerance?: string;
  marketOrderLimitPrice?: string;
}

export function buildSlippageRequestFields(
  state: SlippageState,
  opts: {
    priceType: 'market' | 'trigger' | 'limit';
    referencePrice: string;
    side?: SlippageSide;
    bbo?: string;
  },
): SlippageRequestFields {
  if (!state.enabled || opts.priceType !== 'market') return {};

  if (state.mode === 'percent') {
    if (state.value === '' || !Num(state.value).isFinite()) return {};
    return { maxSlippageTolerance: percentToRatio(state.value) };
  }

  const reference = Num(opts.referencePrice);
  if (!reference.isFinite() || reference.isLessThanOrEqualTo(0)) return {};
  if (opts.side !== 'Long' && opts.side !== 'Short') return {};
  const bbo = Num(opts.bbo ?? '');
  if (!bbo.isFinite() || bbo.isLessThanOrEqualTo(0)) return {};

  // marketOrderLimitPrice = crossing-side BBO ± the user's RAW spread (no client cap). The reference±5%
  // limit is enforced server-side. Spread defaults to 0 (→ limit = BBO) when empty/invalid/≤0.
  const rawSpread = Num(state.value);
  const spread = rawSpread.isFinite() && rawSpread.isGreaterThan(0) ? rawSpread : Num('0');
  const limit = opts.side === 'Long' ? bbo.plus(spread) : bbo.minus(spread);
  return { marketOrderLimitPrice: limit.toString() };
}
